// A4 invoice table for jsPDF that starts new pages (owner, 26 Sep 2026).
//
// The consolidated Traffic / Salik / Parking invoices put every line in ONE table cell and
// never called addPage: 50 fines made a 270 mm cell starting 110 mm down the page, so the
// lines after roughly the 35th and the TOTAL row were printed below the paper. Lines were also
// cut at 80 characters. The Corporate invoice broke pages for its items only, so its total
// and footer could still run off the page.
//
// Here a row that does not fit continues on the next page (the header is repeated), long
// text wraps inside its column instead of being cut, the total row is kept whole and printed
// after the last line, and every cell - header, text and amounts - is centred in its column
// (owner rule: every table cell is centred, numbers included).

export const PAGE_TOP = 40;          // first line under the letterhead on a continuation page
export const PAGE_BOTTOM = 262;      // last line above the letterhead footer

/** jsPDF's own wrapping when present (fake docs in tests may not measure text). */
const wrap = (doc, text, width) => (typeof doc.splitTextToSize === 'function'
    ? doc.splitTextToSize(String(text), width)
    : [String(text)]);

/**
 * Draws the table and returns the y under it.
 *
 * @param {object} doc      jsPDF document
 * @param {object} o
 * @param {number} o.x      left edge
 * @param {number} o.y      top edge of the header row
 * @param {{title: string, width: number}[]} o.columns
 * @param {(string|string[])[][]} o.rows   one array of cells per row; a cell may hold several lines
 * @param {{label: string, value: string, labelFrom?: number}|Array} [o.total]  label spans the columns
 *        from ``labelFrom`` (default 1) to the one before last; the value sits in the last column. An array
 *        gives several total rows (Net / VAT / TOTAL), the last one being the grand total.
 * @param {() => number} o.newPage   adds a page (with its letterhead) and returns the y to go on at
 */
export function drawInvoiceTable(doc, {
    x, y, columns, rows, total, newPage,
    maxY = PAGE_BOTTOM, lineHeight = 5, padding = 3, minRowHeight = 10, fontSize = 11, headerHeight = 10,
}) {
    const widths = columns.map((c) => c.width);
    const colX = [];
    widths.reduce((left, w, i) => { colX[i] = left; return left + w; }, x);
    const tableW = widths.reduce((a, b) => a + b, 0);
    const centre = (i) => colX[i] + widths[i] / 2;
    const verticals = (top, h, from = 1) => colX.slice(from).forEach((cx) => doc.line(cx, top, cx, top + h));
    const linesAt = (top, h, lines, i) => {
        const first = top + (h - lines.length * lineHeight) / 2 + lineHeight * 0.75;
        lines.forEach((line, k) => doc.text(String(line), centre(i), first + k * lineHeight, { align: 'center' }));
    };

    const header = () => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', 'bold');
        doc.setFillColor(240, 240, 240);
        doc.rect(x, y, tableW, headerHeight, 'F');
        columns.forEach((c, i) => linesAt(y, headerHeight, [c.title], i));
        doc.rect(x, y, tableW, headerHeight, 'S');
        verticals(y, headerHeight);
        y += headerHeight;
        doc.setFont('helvetica', 'normal');
    };
    const nextPage = () => { y = newPage(); header(); };

    header();
    rows.forEach((row) => {
        const cells = row.map((cell, i) => (Array.isArray(cell) ? cell : [cell ?? ''])
            .flatMap((part) => wrap(doc, part, widths[i] - 2 * padding)));
        // The tallest cell may run over pages; the others are printed with its first part.
        const main = cells.reduce((best, c, i) => (c.length > cells[best].length ? i : best), 0);
        const others = Math.max(1, ...cells.map((c, i) => (i === main ? 0 : c.length)));
        const lines = cells[main];
        let from = 0;
        let first = true;
        for (;;) {
            const room = Math.floor((maxY - y - 2 * padding) / lineHeight);
            const left = lines.length - from;
            const need = first ? Math.max(left, others) : left;
            let take = left;
            if (need > room) {
                // do not start a row with a sliver at the foot of a page
                const smallest = first ? Math.max(others, Math.min(left, 3)) : 1;
                if (room < smallest) { nextPage(); continue; }
                take = room;
            }
            const shown = first ? Math.max(take, others) : take;
            const h = Math.max(minRowHeight, shown * lineHeight + 2 * padding);
            doc.setFont('helvetica', 'normal');
            for (let i = 0; i < cells.length; i += 1) {
                const part = i === main ? cells[i].slice(from, from + take) : (first ? cells[i] : []);
                if (part.length) linesAt(y, h, part, i);
            }
            doc.rect(x, y, tableW, h, 'S');
            verticals(y, h);
            y += h;
            from += take;
            first = false;
            if (from >= lines.length) break;
            nextPage();
        }
    });

    // The total rows: one row, or several (Net / VAT 5% / TOTAL on a tax invoice - FIN-12) kept together on
    // one page; the last row is the grand total (darker, bold).
    const totals = !total ? [] : (Array.isArray(total) ? total : [total]);
    if (totals.length) {
        const h = minRowHeight;
        if (y + h * totals.length > maxY) nextPage();
        const last = columns.length - 1;
        totals.forEach((t, k) => {
            const grand = k === totals.length - 1;
            const labelX = colX[t.labelFrom ?? 1];
            const labelW = colX[last] - labelX;
            if (grand) doc.setFillColor(220, 220, 220); else doc.setFillColor(240, 240, 240);
            doc.rect(labelX, y, labelW + widths[last], h, 'F');
            doc.setFont('helvetica', grand ? 'bold' : 'normal');
            doc.text(String(t.label), labelX + labelW / 2, y + h / 2 + lineHeight * 0.35, { align: 'center' });
            doc.text(String(t.value), centre(last), y + h / 2 + lineHeight * 0.35, { align: 'center' });
            doc.rect(labelX, y, labelW, h, 'S');
            doc.rect(colX[last], y, widths[last], h, 'S');
            y += h;
        });
        doc.setFont('helvetica', 'normal');
    }
    return y;
}

/** ``y`` when ``need`` mm still fit on this page, otherwise the top of a new page. */
export function ensureSpace(y, need, newPage, maxY = PAGE_BOTTOM) {
    return y + need > maxY ? newPage() : y;
}

/** A continuation page with the letterhead drawn on it. */
export function letterheadPage(doc, imgData, top = PAGE_TOP) {
    return () => {
        doc.addPage();
        if (imgData) doc.addImage(imgData, 'JPEG', 0, 0, 210, 297);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        return top;
    };
}
